// React
import React from "react";
import PropTypes from "prop-types";
// Helpers
import { isString } from "@mefisto/utils";
// Components
import { classnames } from "ui/classnames";
import { makeStyles, TableCell, Typography } from "@material-ui/core";

////////////////////////////////////////////////////
/// Styles
////////////////////////////////////////////////////

const useStyles = makeStyles((theme) => ({
  root: {
    border: theme.palette.border.surface.main,
    borderColor: "transparent",
    borderLeft: "none",
    borderRight: "none",
    transition: theme.transitions.create(["background"], {
      duration: theme.transitions.duration.short,
    }),
  },
  selected: {
    borderColor: theme.palette.border.surface.color,
  },
  highlighted: {
    borderColor: theme.palette.border.surface.color,
  },
  sticky: {
    position: "sticky",
    whiteSpace: "nowrap",
    // A small trick to size the cell based on the content
    width: 1,
    right: 0,
    background: "inherit",
  },
}));

////////////////////////////////////////////////////
/// Component
////////////////////////////////////////////////////

const TableRowItem = ({
  variant,
  align,
  size,
  wrap,
  color,
  sticky,
  children,
  __renderProps: { index, draggable, selected, highlighted } = {},
  ...rest
}) => {
  // Styles
  const classes = useStyles();
  // Render
  return (
    <TableCell
      padding={index === 0 && draggable ? "checkbox" : "normal"}
      align={align}
      size={size}
      className={classnames(classes.root, {
        [classes.sticky]: sticky,
        [classes.selected]: selected,
        [classes.highlighted]: highlighted,
      })}
      {...rest}
    >
      {isString(children) ? (
        <Typography
          component="div"
          color={color}
          noWrap={!wrap}
          variant={variant === "highlight" ? "subtitle2" : "inherit"}
        >
          {children}
        </Typography>
      ) : (
        children
      )}
    </TableCell>
  );
};

TableRowItem.propTypes = {
  /**
   * Specify the item type
   */
  variant: PropTypes.oneOf(["default", "highlight"]),
  /**
   * Set content align
   */
  align: PropTypes.oneOf(["center", "inherit", "justify", "left", "right"]),
  /**
   * Specify the size of the item.
   */
  size: PropTypes.oneOf(["medium", "small"]),
  /**
   * Set to `true` when content should wrap
   */
  wrap: PropTypes.bool,
  /**
   * The color of the component. It supports those theme colors that make sense for this component.
   */
  color: PropTypes.oneOf([
    "initial",
    "inherit",
    "primary",
    "secondary",
    "textPrimary",
    "textSecondary",
    "white",
    "error",
  ]),
  /**
   * Set to `true` when the table cell suppose to be sticky
   */
  sticky: PropTypes.bool,
  /**
   * The row item contents
   */
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
};

export default TableRowItem;
